import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Alert } from '@mui/material';
import { httpOnlySecurityService } from '../../../application/iam';
import { useUser, userQueryKey } from '../../../hooks/useUser';
import { useQueryClient, useIsFetching } from '@tanstack/react-query';
import { markOAuthRedirect } from '../../api/request';
import { UserRoles } from '../../constants/userConstants';

/**
 * ProtectedRoute Component
 * 
 * A wrapper component that provides authentication and role-based authorization for routes.
 * 
 * @example
 * // Basic usage - protects route for any authenticated user
 * <ProtectedRoute>
 *   <YourComponent />
 * </ProtectedRoute>
 * 
 * @example
 * // Role-based protection - only users with ADMIN role can access
 * <ProtectedRoute allowedRoles={[UserRoles.ADMIN]}>
 *   <AdminOnlyComponent />
 * </ProtectedRoute>
 * 
 * @example
 * // Multiple roles - users with either ADMIN or SUPER_USER roles can access
 * <ProtectedRoute allowedRoles={[UserRoles.ADMIN, UserRoles.SUPER_USER]}>
 *   <RestrictedComponent />
 * </ProtectedRoute>
 * 
 * @example
 * // Custom redirect path
 * <ProtectedRoute redirectPath="/login">
 *   <YourComponent />
 * </ProtectedRoute>
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: Array<typeof UserRoles[keyof typeof UserRoles]>; // Using keyof to get the allowed roles from the enum
  redirectPath?: string; // Custom redirect path for unauthorized users
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  allowedRoles = [], // Default is empty array, which means all authenticated users are allowed
  redirectPath = '/' 
}) => {
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  // Use React Query's useIsFetching to check if user query is already fetching
  // This prevents duplicate requests when multiple components mount simultaneously
  const queryClient = useQueryClient();
  const cachedUserData = queryClient.getQueryData(userQueryKey);
  const isUserFetching = useIsFetching({ queryKey: userQueryKey }) > 0;
  // Only fetch if data doesn't exist in cache AND no active fetch is happening
  const shouldFetchUser = !cachedUserData && !isUserFetching;
  const { data: userInfo, isLoading: isLoadingUser, refetch: refetchUser } = useUser(shouldFetchUser);

  // Redirect function to ensure consistent redirection
  const redirectToHome = (reason: string) => {
    navigate(redirectPath);
  };

  // Unauthorized redirect function
  const redirectToUnauthorized = () => {
    navigate('/unauthorized', { state: { from: location.pathname } });
  };

  // Helper function to check if user has required role
  const checkUserRoleAccess = (userRole: string | undefined, requiredRoles: Array<typeof UserRoles[keyof typeof UserRoles]>) => {
    // If no specific roles are required, allow access
    if (requiredRoles.length === 0) return true;
    
    // If user has no role but route requires roles, deny access
    if (!userRole) return false;
    
    // Check if user's role is in the list of allowed roles
    return requiredRoles.includes(userRole as typeof UserRoles[keyof typeof UserRoles]);
  };

  // Primary authentication and authorization check
  useEffect(() => {
    let isMounted = true;
    let checkTimer: number;
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000; // 1 second delay between retries

    // Detect if we're on /home without user info - likely OAuth redirect
    const isHomePage = location.pathname === '/home' || location.pathname === '/home/';
    if (isHomePage && !userInfo && !isLoadingUser) {
      // Mark as OAuth redirect to give grace period for cookies to be available
      markOAuthRedirect();
    }

    // Wait for user data to load from React Query before making authentication decisions
    if (isLoadingUser) {
      // Still loading from React Query, don't make decisions yet
      return;
    }

    // First check: User context from React Query (preferred - uses cache, prevents duplicate requests)
    // Check for any identifying field (email is required, but also check id or samId as fallback)
    if (userInfo && (userInfo.email || userInfo.id || userInfo.samId)) {
      setIsAuthenticated(true);
      
      // Check role authorization
      const hasRequiredRole = checkUserRoleAccess(
        userInfo.role, 
        allowedRoles
      );
      setIsAuthorized(hasRequiredRole);
      setIsChecking(false);
      
      // If not authorized, redirect to unauthorized page
      if (!hasRequiredRole && allowedRoles.length > 0) {
        redirectToUnauthorized();
      }
      
      return;
    }

    // Second check: Token validity (fallback only if React Query doesn't have data)
    // This should rarely happen since useUser() is enabled and will fetch
    if (httpOnlySecurityService) {
      const tokenValid = !httpOnlySecurityService.isTokenExpired();
      if (tokenValid) {
        // Use React Query's refetch instead of direct axios call to prevent duplicates
        // This handles edge cases like OAuth redirects where data might not be cached yet
        const verifyAuthentication = async (attemptNumber: number = 0): Promise<void> => {
          try {
            // Use useUser hook's refetch mechanism instead of direct axios call
            const result = await refetchUser();
            const userData = result.data;
            
            if (isMounted && userData && (userData.email || userData.id || userData.samId)) {
              setIsAuthenticated(true);
              
              // Check role authorization
              const userRole = userData?.role as typeof UserRoles[keyof typeof UserRoles] | undefined;
              const hasRequiredRole = checkUserRoleAccess(userRole, allowedRoles);
              setIsAuthorized(hasRequiredRole);
              
              setIsChecking(false);
              
              // If not authorized, redirect to unauthorized page
              if (!hasRequiredRole && allowedRoles.length > 0) {
                redirectToUnauthorized();
              }
            } else if (isMounted && !userData) {
              // Refetch succeeded but no user data - authentication failed
              setAuthError('No user data received');
              setIsAuthenticated(false);
              setIsAuthorized(false);
              setIsChecking(false);
              redirectToHome('No user data received');
            }
          } catch (error: any) {
            console.error(`Authentication verification failed (attempt ${attemptNumber + 1}):`, error);
            
            // Only retry once for OAuth redirect scenarios on /home
            // Axios interceptor already handles refresh retries
            const isHomePage = location.pathname === '/home' || location.pathname === '/home/';
            const is401Error = error?.response?.status === 401;
            const shouldRetry = attemptNumber === 0 && isHomePage && is401Error;
            
            if (shouldRetry && isMounted) {
              // Wait before retrying to allow cookies to be available
              setTimeout(() => {
                if (isMounted) {
                  verifyAuthentication(attemptNumber + 1);
                }
              }, RETRY_DELAY);
              return;
            }
            
            if (isMounted) {
              setAuthError('API verification failed');
              setIsAuthenticated(false);
              setIsAuthorized(false);
              setIsChecking(false);
              redirectToHome('API verification failed');
            }
          }
        };
        
        verifyAuthentication();
      } else {
        // Token is invalid, but check if we're in OAuth grace period
        // Right after OAuth redirect, token might appear expired but cookies are being set
        const isHomePage = location.pathname === '/home' || location.pathname === '/home/';
        if (isHomePage) {
          // Wait a bit and retry token check once to allow cookies to be available
          setTimeout(() => {
            if (isMounted) {
              const tokenValidRetry = !httpOnlySecurityService.isTokenExpired();
              if (tokenValidRetry) {
                // Token is now valid, proceed with verification
                const verifyAuthentication = async () => {
                  try {
                    // Use useUser hook's refetch mechanism instead of direct axios call
                    const result = await refetchUser();
                    const userData = result.data;
                    
                    if (isMounted && userData && (userData.email || userData.id || userData.samId)) {
                      setIsAuthenticated(true);
                      const userRole = userData?.role as typeof UserRoles[keyof typeof UserRoles] | undefined;
                      const hasRequiredRole = checkUserRoleAccess(userRole, allowedRoles);
                      setIsAuthorized(hasRequiredRole);
                      setIsChecking(false);
                      if (!hasRequiredRole && allowedRoles.length > 0) {
                        redirectToUnauthorized();
                      }
                    } else if (isMounted && !userData) {
                      // Refetch succeeded but no user data - authentication failed
                      setAuthError('No user data received');
                      setIsAuthenticated(false);
                      setIsAuthorized(false);
                      setIsChecking(false);
                      redirectToHome('No user data received');
                    }
                  } catch (error) {
                    console.error('Authentication verification failed after retry:', error);
                    if (isMounted) {
                      setAuthError('API verification failed');
                      setIsAuthenticated(false);
                      setIsAuthorized(false);
                      setIsChecking(false);
                      redirectToHome('API verification failed');
                    }
                  }
                };
                verifyAuthentication();
              } else {
                // Still invalid after retry, redirect
                if (isMounted) {
                  setAuthError('Token expired or invalid');
                  setIsAuthenticated(false);
                  setIsAuthorized(false);
                  setIsChecking(false);
                  redirectToHome('Token expired or invalid');
                }
              }
            }
          }, RETRY_DELAY);
        } else {
          // Token is invalid, redirect immediately
          if (isMounted) {
            setAuthError('Token expired or invalid');
            setIsAuthenticated(false);
            setIsAuthorized(false);
            setIsChecking(false);
            redirectToHome('Token expired or invalid');
          }
        }
      }
    } else {
      // No security service available, cannot authenticate
      if (isMounted) {
        setAuthError('Security service unavailable');
        setIsAuthenticated(false);
        setIsAuthorized(false);
        setIsChecking(false);
        redirectToHome('Security service unavailable');
      }
    }

    // Set a fallback timer to ensure redirection happens if checks take too long
    checkTimer = window.setTimeout(() => {
      if (isMounted && isChecking) {
        setAuthError('Authentication check timeout');
        setIsChecking(false);
        redirectToHome('Authentication check timeout');
      }
    }, 15000);

    return () => {
      isMounted = false;
      clearTimeout(checkTimer);
    };
  }, [location.pathname, userInfo, isLoadingUser, navigate, allowedRoles, redirectPath, refetchUser]);

  if (isChecking || isLoadingUser) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={40} />
        <Alert severity="info" sx={{ ml: 2 }}>
          Checking authentication... This may take a moment.
        </Alert>
      </Box>
    );
  }

  if (!isAuthenticated) {
    // This will only show briefly before redirection
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={40} />
        <Alert severity="info" sx={{ ml: 2 }}>
          {authError ? `Authentication failed: ${authError}. ` : ''}
          Redirecting to home page...
        </Alert>
      </Box>
    );
  }

  if (!isAuthorized && allowedRoles.length > 0) {
    // This will only show briefly before redirection
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={40} />
        <Alert severity="warning" sx={{ ml: 2 }}>
          You don't have permission to access this page. Redirecting...
        </Alert>
      </Box>
    );
  }

  return <>{children}</>;
}; 